import React from 'react';
import styled from '@emotion/styled';
import { ButtonPrimary, LoadingIndicator } from '@spotify-internal/encore-web';
import { TFunction, useT } from 'client/i18n/hooks/useT';
import { WaveformProcessingStatus } from '../Waveform/types';

type Props = {
  state?: WaveformProcessingStatus;
  onRetry: () => void;
  hideCopy?: boolean;
};

const Container = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 270px;
  padding: 0px 120px;
  color: white;
  text-align: center;
  svg > circle {
    fill: white;
  }
`;

const Title = styled.p`
  color: white;
  font-weight: bold;
`;

const Copy = styled.p`
  margin-top: 12px;
  color: white;
`;

const RetryButton = styled(ButtonPrimary)`
  align-self: center;
  margin-top: 12px;
`;

const ErrorState = ({
  onRetry,
  t,
}: Pick<Props, 'onRetry'> & { t: TFunction }) => (
  <Container>
    <Title>
      {t(
        'SplitSegmentAudioModalScreen.LoadingBar.ErrorMessage',
        'Sorry, something went wrong.',
        'Audio split editor modal loading error message'
      )}
    </Title>
    <RetryButton colorSet="invertedLight" onClick={onRetry}>
      {t('Button.Retry', 'Retry', 'Retry button text')}
    </RetryButton>
  </Container>
);

export function LoadingBar({ state, onRetry, hideCopy }: Props) {
  const t = useT();

  const loadingText =
    state === WaveformProcessingStatus.WAITING
      ? t(
          'SplitSegmentAudioModalScreen.LoadingBar.GeneratingText',
          'Generating',
          'Audio split editor modal loading message'
        )
      : t(
          'SplitSegmentAudioModalScreen.LoadingBar.LoadingText',
          'Loading',
          'Audio split editor modal loading message'
        );

  return state === WaveformProcessingStatus.FAILED ? (
    <ErrorState onRetry={onRetry} t={t} />
  ) : (
    <Container
      aria-busy="true"
      aria-live="polite"
      role="alert"
      aria-label={loadingText}
    >
      <LoadingIndicator indicatorSize={LoadingIndicator.sm} />
      {!hideCopy && (
        <Copy>
          {t(
            'SplitSegmentAudioModalScreen.LoadingBar.Copy',
            'You can use this tool to split your audio into multiple segments. Then use the episode builder to move or insert other segments.',
            'Audio split editor modal loading message'
          )}
        </Copy>
      )}
    </Container>
  );
}
